"use client";

import { useEffect, type ReactNode } from "react";
import { animate, motion, useMotionValue, useReducedMotion, useTransform } from "framer-motion";
import {
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleDashed,
  Clock,
  LayoutGrid,
  PanelRightOpen,
  Plus,
  Square,
  Trash2,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Reveal, reveal, SceneRoot, SCENE_EASE as EASE } from "../scenes/productScene";
import styles from "./tradingJournal.module.scss";
import { INBOX_DAYS, INBOX_ROWS, INBOX_UI, WIZARD_UI } from "./tradingJournalData";

/**
 * HTML rebuild of the trading-journal artwork (Figma 3165:29366 desktop, 3205:61792 mobile):
 * the journal inbox dashboard with the "New Trade" wizard floating over it.
 *
 * Sizes are Figma px ÷ 8 in rem (see `scenes/productScene.tsx`). The reveal is one choreographed,
 * scroll-triggered pass (opacity / transform only) that ends in a still frame; the only loop is
 * the soft breathing outline on the selected day, which pauses off screen and under reduced motion.
 */

const T = {
  inbox: 0,
  days: 0.35,
  dayStep: 0.07,
  selected: 0.95,
  toolbar: 0.55,
  rows: 0.7,
  rowStep: 0.1,
  wizard: 0.45,
  fields: 0.8,
  fieldStep: 0.06,
  direction: 1.75,
  entryRow: 2.0,
  confirm: 2.2,
  pnl: 2.45,
} as const;

/* ----------------------------------------------------------------------------------------- */
/* Inbox dashboard (background layer)                                                         */
/* ----------------------------------------------------------------------------------------- */

function InboxDashboard() {
  const [crumbRoot, crumbSection, crumbPage] = INBOX_UI.breadcrumb;
  return (
    <Reveal
      cue={{ delay: T.inbox, y: 48, duration: 1 }}
      className="absolute left-[59.125rem] top-[11.75rem] h-[122.5rem] w-[161.75rem] overflow-hidden rounded-tl-[1.5rem] border border-white/10 bg-black">
      {/* Top bar */}
      <div className="flex h-[7rem] items-center gap-[1.56rem] border-b border-white/10 pl-[2.4rem] text-[1.37rem] leading-none text-white/40">
        <PanelRightOpen
          className="h-[2.34rem] w-[2.34rem]"
          strokeWidth={1.5}
        />
        <span className="h-[1.2rem] w-px bg-white/15" />
        <span>{crumbRoot}</span>
        <ChevronRight
          className="h-[1.2rem] w-[1.2rem]"
          strokeWidth={1.5}
        />
        <span>{crumbSection}</span>
        <ChevronRight
          className="h-[1.2rem] w-[1.2rem]"
          strokeWidth={1.5}
        />
        <span className="text-white">{crumbPage}</span>
      </div>

      {/* Month switcher */}
      <Reveal
        cue={{ delay: T.days, y: 10 }}
        className="absolute left-[18.9rem] top-[10.1rem] flex items-center gap-[1.5rem] text-[1.56rem] leading-none text-white">
        <NavButton>
          <ChevronLeft
            className="h-[1.4rem] w-[1.4rem]"
            strokeWidth={1.75}
          />
        </NavButton>
        <span>{INBOX_UI.month}</span>
        <NavButton>
          <ChevronRight
            className="h-[1.4rem] w-[1.4rem]"
            strokeWidth={1.75}
          />
        </NavButton>
      </Reveal>

      {/* Day cards */}
      <ul className="absolute left-[8.75rem] top-[15.25rem] m-0 flex list-none gap-[2.08rem] p-0">
        {INBOX_DAYS.map((d, i) => (
          <Reveal
            key={d.day}
            as="li"
            cue={{ delay: T.days + i * T.dayStep, y: 14 }}
            className={cn(
              "relative flex h-[15.5rem] w-[15.5rem] flex-col items-center rounded-[1rem] border border-white/10 bg-[#0a0a0a] pt-[3rem]",
              d.muted && "opacity-50",
            )}>
            {d.selected && (
              <>
                <motion.span
                  variants={reveal}
                  custom={{ delay: T.selected, scale: 0.94, duration: 0.6 }}
                  className="pointer-events-none absolute -inset-px rounded-[1rem] border border-[var(--journal-orange)]"
                />
                <span
                  className={cn(
                    styles.glow,
                    "pointer-events-none absolute -inset-px rounded-[1rem] shadow-[0_0_2.4rem_rgba(255,144,3,0.35)]",
                  )}
                />
              </>
            )}
            <span className="text-[1.56rem] leading-none text-white/50">{d.weekday}</span>
            <span className="mt-[2.2rem] text-[3.12rem] font-medium leading-none text-white">{d.day}</span>
            <motion.span
              variants={reveal}
              custom={{ delay: T.days + 0.3 + i * T.dayStep, scale: 0.7, duration: 0.5 }}
              className="mt-[2.1rem] rounded-full bg-[rgba(50,200,112,0.14)] px-[1.25rem] py-[0.5rem] text-[1.17rem] font-medium leading-none text-[var(--journal-green)]">
              {d.pnl}
            </motion.span>
          </Reveal>
        ))}
      </ul>

      {/* List toolbar */}
      <Reveal
        cue={{ delay: T.toolbar, y: 10 }}
        className="absolute left-[8.75rem] top-[39.25rem] flex h-[4rem] w-[116.6rem] items-center text-[1.37rem] leading-none">
        <span className="text-white">{INBOX_UI.listTitle}</span>
        <span className="ml-auto flex items-center gap-[1.25rem] pr-0">
          <span className="flex h-[4rem] items-center gap-[1rem] rounded-[0.5rem] border border-white/10 px-[1.5rem] text-white/80">
            {INBOX_UI.ignoreAll}
            <LayoutGrid
              className="h-[1.5rem] w-[1.5rem]"
              strokeWidth={1.5}
            />
          </span>
          <span className="flex h-[4rem] items-center gap-[1rem] rounded-[0.5rem] bg-[var(--journal-orange)] px-[1.5rem] font-medium text-white">
            {INBOX_UI.process}
            <ChevronRight
              className="h-[1.5rem] w-[1.5rem]"
              strokeWidth={2}
            />
          </span>
        </span>
      </Reveal>

      {/* Tabs */}
      <Reveal
        cue={{ delay: T.toolbar + 0.1, y: 10 }}
        className="absolute left-0 right-0 top-[45.5rem] border-b border-white/10 pl-[8.4rem] text-[1.37rem] leading-none">
        <div className="flex gap-[3rem]">
          <span className="relative flex items-center gap-[1rem] pb-[1.6rem] text-white">
            <Clock
              className="h-[1.6rem] w-[1.6rem]"
              strokeWidth={1.5}
            />
            {INBOX_UI.tabs[0]}
            <span className="absolute inset-x-0 -bottom-px h-[0.25rem] bg-[var(--journal-orange)]" />
          </span>
          <span className="flex items-center gap-[1rem] pb-[1.6rem] text-white/60">
            <CircleDashed
              className="h-[1.6rem] w-[1.6rem]"
              strokeWidth={1.5}
            />
            {INBOX_UI.tabs[1]}
          </span>
        </div>
      </Reveal>

      {/* Table */}
      <div className="absolute left-0 right-0 top-[51.25rem] text-[1.37rem] leading-none">
        <Reveal
          cue={{ delay: T.rows, y: 8 }}
          className="grid h-[4.5rem] grid-cols-[21rem_29.6rem_29rem_27.3rem_1fr] items-center border-b border-white/10 pl-[5.5rem] text-white/50">
          {INBOX_UI.columns.map((c) => (
            <span key={c}>{c}</span>
          ))}
        </Reveal>
        {INBOX_ROWS.map((r, i) => (
          <Reveal
            key={r.amount}
            cue={{ delay: T.rows + 0.15 + i * T.rowStep, x: -16 }}
            className="relative grid h-[5.5rem] grid-cols-[21rem_29.6rem_29rem_27.3rem_1fr] items-center border-b border-white/5 pl-[5.5rem] text-white/80">
            <span
              className={cn(
                "absolute left-[5.5rem] top-1/2 h-[3rem] w-[0.25rem] -translate-y-1/2 rounded-full",
                r.side === "buy" ? "bg-[var(--journal-green)]" : "bg-[var(--journal-red)]",
              )}
            />
            <span className="pl-[5.1rem]">
              <span className="rounded-full bg-white/5 px-[1.4rem] py-[0.65rem] text-[1.17rem] capitalize text-white">
                {r.side}
              </span>
            </span>
            <span className="text-[1.56rem] text-white">{r.amount}</span>
            <span>{r.price}</span>
            <span>{r.date}</span>
            <span />
          </Reveal>
        ))}
      </div>
    </Reveal>
  );
}

function NavButton({ children }: { children: ReactNode }) {
  return (
    <span className="grid h-[2.34rem] w-[2.34rem] place-items-center rounded-[0.5rem] border border-white/10 bg-[#0a0a0a] text-white">
      {children}
    </span>
  );
}

/* ----------------------------------------------------------------------------------------- */
/* New-trade wizard (foreground layer)                                                        */
/* ----------------------------------------------------------------------------------------- */

function Label({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cn("block text-[0.935rem] leading-none text-white/50", className)}>{children}</span>;
}

function Field({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        "flex h-[2.5rem] items-center gap-[0.8rem] rounded-[0.53rem] bg-white/5 px-[1.2rem] text-[0.935rem] leading-none text-white",
        className,
      )}>
      {children}
    </div>
  );
}

const ORDER_GRID = "grid grid-cols-[9.4rem_6.6rem_8.4rem_8.6rem_7.4rem_1fr] items-center";

function OrderTable({
  columns,
  rows,
  tone,
  delay,
  children,
}: {
  columns: readonly string[];
  rows: readonly { price: string; quantity: string; type: string; date: string; time: string }[];
  tone: "entry" | "exit";
  delay: number;
  children?: ReactNode;
}) {
  const toneClass = tone === "entry" ? "text-[var(--journal-green)]" : "text-[var(--journal-red)]";
  return (
    <div className="flex flex-col gap-[0.9rem] rounded-[0.78rem] bg-white/5 p-[1.25rem] leading-none">
      <div className={cn(ORDER_GRID, "px-[0.5rem] text-[0.8rem] text-white/50")}>
        {columns.map((c) => (
          <span key={c}>{c}</span>
        ))}
      </div>
      {rows.map((r, i) => (
        <Reveal
          key={r.price}
          cue={{ delay: delay + i * 0.12, y: 6, duration: 0.5 }}
          className={cn(ORDER_GRID, "h-[2.2rem] px-[0.5rem] text-[0.935rem] text-white")}>
          <span className={toneClass}>{r.price}</span>
          <span className={toneClass}>{r.quantity}</span>
          <span>{r.type}</span>
          <span>{r.date}</span>
          <span>{r.time}</span>
          <Trash2
            className="h-[1.1rem] w-[1.1rem] justify-self-end text-white/50"
            strokeWidth={1.5}
          />
        </Reveal>
      ))}
      {children}
    </div>
  );
}

function NetPnl({ play }: { play: boolean }) {
  const reduced = useReducedMotion();
  const target = WIZARD_UI.netPnl.value;
  // Starts at 0 on both server and client (no hydration mismatch); reduced motion jumps straight
  // to the final figure.
  const value = useMotionValue(0);
  const text = useTransform(value, (v) => `${Math.round(v).toLocaleString("de-DE")}$`);
  useEffect(() => {
    if (!play) return;
    if (reduced) {
      value.set(target);
      return;
    }
    const controls = animate(value, target, { delay: T.pnl, duration: 1.1, ease: [0.16, 1, 0.3, 1] });
    return () => controls.stop();
  }, [play, reduced, target, value]);
  return <motion.span className="text-[1.07rem] font-medium text-[var(--journal-green)]">{text}</motion.span>;
}

export function NewTradeWizard({ play }: { play: boolean }) {
  const { account, pair, stopLoss, takeProfit, entries, exits } = WIZARD_UI;
  let field = 0;
  const next = () => T.fields + field++ * T.fieldStep;

  return (
    <Reveal
      cue={{ delay: T.wizard, x: -40, y: 16, duration: 1 }}
      className="absolute left-[5rem] top-[3.375rem] flex h-[67.25rem] w-[72.125rem] overflow-hidden rounded-[1.5rem] border border-white/10 bg-[#0d0d0d] shadow-[0_2.5rem_8rem_rgba(0,0,0,0.55)]">
      {/* Steps sidebar */}
      <aside className="flex w-[17.5rem] shrink-0 flex-col border-r border-white/10 bg-[#080808] px-[1.5rem] pt-[1.75rem] leading-none">
        <span className="text-[1.56rem] font-medium text-white">{WIZARD_UI.title}</span>
        <span className="mt-[0.7rem] text-[0.935rem] text-white/50">{WIZARD_UI.subtitle}</span>
        <ol className="relative m-0 mt-[3.2rem] flex list-none flex-col gap-[2.9rem] p-0 pl-[0.375rem]">
          {WIZARD_UI.steps.map((s, i) => (
            <Reveal
              key={s.title}
              as="li"
              cue={{ delay: T.fields + i * 0.1, y: 6, duration: 0.5 }}
              className="relative flex items-start gap-[1.1rem]">
              <span
                className={cn(
                  "grid h-[1.5rem] w-[1.5rem] shrink-0 place-items-center rounded-full text-[0.8rem] font-medium",
                  i === 0
                    ? "bg-[var(--journal-orange)] text-white"
                    : "border border-white/10 bg-white/5 text-white/50",
                )}>
                {i + 1}
              </span>
              {i < WIZARD_UI.steps.length - 1 && (
                <span
                  className={cn(
                    "absolute left-[0.7rem] top-[1.9rem] h-[2.1rem] w-px border-l border-dashed",
                    i === 0 ? "border-[rgba(255,144,3,0.7)]" : "border-white/20",
                  )}
                />
              )}
              <span className="flex flex-col gap-[0.5rem]">
                <span className="text-[0.935rem] font-medium text-white">{s.title}</span>
                <span className="text-[0.8rem] text-white/50">{s.text}</span>
              </span>
            </Reveal>
          ))}
        </ol>
      </aside>

      {/* Form */}
      <div className="relative flex min-w-0 flex-1 flex-col bg-[#0f0f0f] px-[3.75rem] pb-[6rem] pt-[2.6rem] [&>*]:shrink-0">
        <Reveal cue={{ delay: next(), y: 8 }}>
          <Label>{account.label}</Label>
          <Field className="mt-[0.5rem]">
            <span>{account.value}</span>
            <span className="rounded-full bg-[rgba(93,122,210,0.35)] px-[0.9rem] py-[0.35rem] text-[0.8rem] text-[#aebdf5]">
              {account.tag}
            </span>
            <ChevronDown
              className="ml-auto h-[1.1rem] w-[1.1rem] text-white/60"
              strokeWidth={1.5}
            />
          </Field>
        </Reveal>

        <Reveal
          cue={{ delay: next(), y: 8 }}
          className="mt-[1.5rem]">
          <Label>{pair.label}</Label>
          <Field className="mt-[0.5rem]">
            <span>{pair.value}</span>
            <ChevronDown
              className="ml-auto h-[1.1rem] w-[1.1rem] text-white/60"
              strokeWidth={1.5}
            />
          </Field>
        </Reveal>

        {/* Long / Short segmented control: the green thumb slides from Short to Long. */}
        <Reveal
          cue={{ delay: next(), y: 8 }}
          className="relative mt-[2.5rem] grid h-[2.75rem] grid-cols-2 overflow-hidden rounded-full bg-white/5 text-[0.935rem] font-medium leading-none">
          <motion.span
            variants={{
              hidden: { x: "100%" },
              show: { x: "0%", transition: { delay: T.direction, duration: 0.65, ease: EASE } },
            }}
            className="absolute inset-y-0 left-0 w-1/2 rounded-full bg-[var(--journal-green)]"
          />
          <span className="relative flex items-center justify-center gap-[0.8rem] text-white">
            <TrendingUp
              className="h-[1.1rem] w-[1.1rem]"
              strokeWidth={2}
            />
            {WIZARD_UI.direction[0]}
          </span>
          <span className="relative flex items-center justify-center gap-[0.8rem] text-white/80">
            <TrendingDown
              className="h-[1.1rem] w-[1.1rem]"
              strokeWidth={2}
            />
            {WIZARD_UI.direction[1]}
          </span>
        </Reveal>

        <Reveal
          cue={{ delay: next(), y: 8 }}
          className="mt-[2.75rem] grid grid-cols-2 gap-[1.25rem]">
          <div>
            <Label>{stopLoss.label}</Label>
            <Field className="mt-[0.5rem] text-white/50">{stopLoss.value}</Field>
          </div>
          <div>
            <Label>{takeProfit.label}</Label>
            <Field className="mt-[0.5rem] text-white/50">{takeProfit.value}</Field>
          </div>
        </Reveal>

        <Reveal
          cue={{ delay: next(), y: 8 }}
          className="mt-[2rem]">
          <Label className="mb-[0.9rem]">{entries.label}</Label>
          <OrderTable
            columns={entries.columns}
            rows={entries.rows}
            tone="entry"
            delay={T.entryRow}>
            <div className="flex items-center gap-[0.6rem]">
              {entries.inputs.map((p) => (
                <span
                  key={p}
                  className="flex h-[2.5rem] flex-1 items-center rounded-[0.5rem] border border-white/10 bg-white/5 px-[1rem] text-[0.8rem] leading-none text-white/40">
                  {p}
                </span>
              ))}
              <motion.span
                variants={{
                  hidden: { scale: 1 },
                  show: { scale: [1, 1.22, 1], transition: { delay: T.confirm, duration: 0.55, ease: "easeInOut" } },
                }}
                className="grid h-[2.5rem] w-[2.5rem] shrink-0 place-items-center rounded-[0.5rem] bg-[var(--journal-orange)] text-white">
                <Check
                  className="h-[1.3rem] w-[1.3rem]"
                  strokeWidth={2.5}
                />
              </motion.span>
            </div>
          </OrderTable>
        </Reveal>

        <Reveal
          cue={{ delay: next(), y: 8 }}
          className="mt-[1.8rem]">
          <Label className="mb-[0.9rem]">{exits.label}</Label>
          <OrderTable
            columns={exits.columns}
            rows={exits.rows}
            tone="exit"
            delay={T.entryRow + 0.25}>
            <span className="flex h-[2.4rem] items-center justify-center gap-[0.6rem] rounded-[0.5rem] bg-white/5 text-[0.8rem] leading-none text-white">
              <Plus
                className="h-[1rem] w-[1rem]"
                strokeWidth={2}
              />
              {exits.add}
            </span>
          </OrderTable>
        </Reveal>

        <Reveal
          cue={{ delay: next(), y: 8 }}
          className="mt-[3rem] flex gap-[3.75rem] text-[0.8rem] leading-none text-white/70">
          {WIZARD_UI.options.map((o) => (
            <span
              key={o}
              className="flex items-center gap-[0.7rem]">
              <Square
                className="h-[1rem] w-[1rem] text-white/50"
                strokeWidth={1.5}
              />
              {o}
            </span>
          ))}
        </Reveal>

        {/* Footer */}
        <Reveal
          cue={{ delay: next(), y: 8 }}
          className="absolute inset-x-0 bottom-0 flex h-[6rem] items-center border-t border-white/10 px-[3.75rem] leading-none">
          <span className="flex h-[2.5rem] items-center gap-[0.8rem] rounded-[0.5rem] bg-white/5 px-[1.4rem] text-[0.935rem] text-white">
            <ChevronLeft
              className="h-[1.1rem] w-[1.1rem]"
              strokeWidth={2}
            />
            {WIZARD_UI.back}
          </span>
          <Reveal
            cue={{ delay: T.pnl, y: 6, duration: 0.6 }}
            as="span"
            className="ml-[6.5rem] flex items-center gap-[1rem] text-[0.8rem] text-white/50">
            {WIZARD_UI.netPnl.label}
            <NetPnl play={play} />
          </Reveal>
          <span className="ml-[3.75rem] flex items-center gap-[1rem] text-[0.8rem] text-white/50">
            {WIZARD_UI.avgRr.label}
            <span className="text-[1.07rem] font-medium text-white">{WIZARD_UI.avgRr.value}</span>
          </span>
          <motion.span
            variants={{
              hidden: { scale: 1 },
              show: { scale: [1, 1.06, 1], transition: { delay: T.pnl + 0.35, duration: 0.6, ease: "easeInOut" } },
            }}
            className="ml-auto flex h-[2.5rem] items-center gap-[0.8rem] rounded-[0.5rem] bg-[var(--journal-orange)] px-[1.6rem] text-[0.935rem] font-medium text-white">
            {WIZARD_UI.next}
            <ChevronRight
              className="h-[1.1rem] w-[1.1rem]"
              strokeWidth={2.5}
            />
          </motion.span>
        </Reveal>
      </div>
    </Reveal>
  );
}

/* ----------------------------------------------------------------------------------------- */
/* Scene                                                                                      */
/* ----------------------------------------------------------------------------------------- */

const SCENE_LABEL = "Trade Stats trading journal: the new-trade form open over the inbox calendar";

/** Desktop composition: inbox behind, wizard in front, inside the 180rem × 76.625rem frame. */
export default function TradingJournalScene({ className }: { className?: string }) {
  return (
    <SceneRoot
      label={SCENE_LABEL}
      sceneClassName={styles.scene}
      className={className}>
      {(play) => (
        <>
          <InboxDashboard />
          <NewTradeWizard play={play} />
        </>
      )}
    </SceneRoot>
  );
}

/** Mobile composition (3205:61792): the wizard alone, enlarged and cropped by the frame. */
export function TradingJournalSceneMobile({ className }: { className?: string }) {
  return (
    <SceneRoot
      label={SCENE_LABEL}
      sceneClassName={styles.scene}
      className={className}>
      {(play) => (
        <div className="absolute left-[-3.6rem] top-[-1.2rem] h-full w-full origin-top-left scale-[1.1]">
          <NewTradeWizard play={play} />
        </div>
      )}
    </SceneRoot>
  );
}
